<script>
import {
  getProduct,
  createProduct,
  getNextAvailable as getNextAvailableApi
} from "@/api/store/ProductsService.js";
import { createSupersession } from "@/api/store/SupersessionsService.js";
import { saveCatalogPrice } from "@/api/store/CatalogPricingService.js";
import { loadPriceGroups } from "@/api/store/PriceGroupsService.js";
import { getSettings } from "@/api/store/SettingsService.js";
import { getBrands } from "@/api/store/BrandsService.js";
import { DEFAULT_DEALER_PROFIT } from "@/utils/priceGroupCalculator.js";
import CatalogPriceForm, {
  emptyCatalogDraft
} from "@/components/product/CatalogPriceForm.vue";

const CATALOG_DEPENDENCIAS = ["B2", "Z"];

const DEPENDENCIA_OPTIONS = [
  { code: "A", name: "A — Standard (stock)" },
  { code: "B", name: "B — Backorder (Isuzu)" },
  { code: "V", name: "V — Backorder (Isuzu V)" },
  { code: "C", name: "C — Quote only" },
  {
    code: "B2",
    name: "B2 — Catalog price",
    comment: "Price comes from Catalog Pricing (no physical stock)"
  },
  {
    code: "Z",
    name: "Z — Catalog price",
    comment: "Price comes from Catalog Pricing (no physical stock)"
  }
];

export default {
  components: { CatalogPriceForm },
  data() {
    return {
      nkrCode: "",
      oe: "",
      unificado: "",
      brand: "",
      brandOptions: [{ code: "", name: "— Select brand —" }],
      dependencia: "A",
      dependenciaOptions: DEPENDENCIA_OPTIONS,
      supersessions: [],
      newSupersession: "",
      priceGroups: [],
      dealerProfit: DEFAULT_DEALER_PROFIT,
      catalogDraft: emptyCatalogDraft(),
      // part_info_local aún no existe para este producto: se ingresa acá y
      // se manda a createProduct(); solo aplica a price_groups != LP45.
      catalogListPrice: 0,
      isValid: null,
      loading: false,
      validatingCode: false,
      rules: {
        nkrCode: [
          v => !!v || "NKR code is required",
          v => !v || v.length >= 8 || "NKR code must be 8 characters"
        ],
        oe: [v => !!v || "OE code is required"],
        unificado: [v => !!v || "Unificado code is required"]
      }
    };
  },
  methods: {
    async loadBrands() {
      try {
        const brands = await getBrands();
        if (Array.isArray(brands)) {
          this.brandOptions = [
            { code: "", name: "— Select brand —" },
            ...brands.map(b => ({
              code: b.name,
              name: b.nameShow || b.name
            }))
          ];
        }
      } catch (e) {
        // silencioso: el select queda solo con la opción vacía
      }
    },
    addSupersession() {
      const code = this.newSupersession.trim().toUpperCase();
      if (!code) {
        return;
      }
      if (this.supersessions.includes(code)) {
        this.$toast.open({
          message: "Supersession already added.",
          type: "warning",
          position: "top-right"
        });
        return;
      }
      this.supersessions.push(code);
      this.newSupersession = "";
    },
    removeSupersession(code) {
      this.supersessions = this.supersessions.filter(c => c !== code);
    },
    async submitForm() {
      if (this.validatingCode || this.loading) {
        return;
      }
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          await createProduct(
            this.nkrCode,
            this.oe,
            this.unificado,
            this.dependencia,
            this.brand,
            // Solo relevante para B2/Z con price_group != LP45: sin esto,
            // part_info_local.list_price queda en 0 y el catalog price
            // 'calculated' falla al no tener list_price maestro.
            this.isCatalog ? this.catalogListPrice : 0
          );

          // Para B2/Z guardamos el precio de catálogo (el producto ya existe
          // en part_info_local con la dependencia, requisito del upsert).
          if (this.isCatalog) {
            await saveCatalogPrice({
              nkrCode: this.nkrCode,
              ...this.catalogDraft
            });
          }

          const failedSupersessions = await this.createSupersessions();

          if (failedSupersessions.length) {
            this.$toast.open({
              message: `Product created, but these supersessions failed: ${failedSupersessions.join(
                ", "
              )}`,
              type: "warning",
              position: "top-right"
            });
          } else {
            this.$toast.open({
              message: `Product created.`,
              type: "success",
              position: "top-right"
            });
          }
          this.$emit("newProductCreated", this.nkrCode);
          this.$emit("close");
        } catch (error) {
          let message = "Error creating product.";
          if (error.response?.data?.error?.message) {
            message = error.response.data.error.message;
          }
          // Race condition: otro usuario/pestaña pudo tomar este mismo código
          // vía "get next available" antes de que se confirmara el insert.
          const isDuplicateEntry =
            /duplicate entry/i.test(message) ||
            error.response?.data?.error?.code === "ER_DUP_ENTRY";
          if (isDuplicateEntry) {
            message = `NKR code ${this.nkrCode} was just taken by another submission. Please get a new available code and try again.`;
            this.isValid = false;
          }
          this.$toast.open({
            message: message,
            type: "error",
            position: "top-right"
          });
          this.loading = false;
          this.$refs.form.addError(message);
        }
      }
    },
    // Las supersessions van atadas al unificado y se crean tras el producto,
    // reusando el endpoint validado. Devuelve los códigos que fallaron.
    async createSupersessions() {
      const failed = [];
      for (const code of this.supersessions) {
        try {
          await createSupersession(code, "", this.unificado);
        } catch (e) {
          failed.push(code);
        }
      }
      return failed;
    },
    async validateNkrCode() {
      const product = await getProduct(this.nkrCode);
      if (!product) {
        this.isValid = true;
        return;
      }
      this.isValid = false;
    },
    async getNextAvailable() {
      if (this.nkrCode.length !== 3) {
        this.$toast.open({
          message: `NKR code to do the verification must be 3 characters.`,
          type: "error",
          position: "top-right"
        });
        return;
      }
      const nextAvailable = await getNextAvailableApi(this.nkrCode);
      if (!nextAvailable) {
        this.$toast.open({
          message: `There are no more available codes.`,
          type: "error",
          position: "top-right"
        });
        return;
      }
      this.nkrCode = nextAvailable;
    }
  },
  computed: {
    isCatalog() {
      return CATALOG_DEPENDENCIAS.includes(this.dependencia);
    },
    status() {
      if (this.isValid === null) {
        return "";
      }
      if (this.isValid) {
        return "Valid";
      }
      return "This product already exist";
    }
  },
  mounted() {
    loadPriceGroups().then(res => {
      this.priceGroups = res;
    });
    getSettings("lp45_dealer_profit").then(res => {
      if (res && res.value !== undefined && res.value !== null) {
        this.dealerProfit = Number(res.value);
      }
    });
    this.loadBrands();
  },
  watch: {
    async nkrCode(val) {
      this.isValid = null;
      if (val.length >= 8) {
        this.validatingCode = true;
        await this.validateNkrCode();
        this.validatingCode = false;
      }
    }
  }
};
</script>
<template>
  <BaseModal id="new-product-modal" :value="true" @input="$emit('close')">
    <template v-slot:header>
      New product
    </template>
    <div class="p-4">
      <BaseForm ref="form" @submit="submitForm">
        <div class="form-layout" :class="{ 'two-col': isCatalog }">
          <div class="col-main">
            <BaseTextField
              v-model="nkrCode"
              type="text"
              label="New product NKR code"
              :rules="rules.nkrCode"
              required
            />
            <div
              v-if="status !== ''"
              class="h-10"
              id="status"
              :class="{
                response: status !== '',
                'response-error': status !== 'Valid' && status !== '',
                'response-success': status == 'Valid'
              }"
            >
              {{ status }}
              <div v-if="validatingCode" class="w-10 h-10">
                <BaseMiniSpinner />
              </div>
            </div>
            <button
              @click="getNextAvailable"
              type="button"
              class="bg-black rounded-xl p-1 text-white my-1"
            >
              get next available
            </button>
            <BaseTextField
              v-model="oe"
              type="text"
              label="OE code"
              :rules="rules.oe"
              required
            />
            <BaseTextField
              v-model="unificado"
              type="text"
              label="Unificado code"
              :rules="rules.unificado"
              required
            />
            <BaseSelectField
              v-model="brand"
              label="Brand"
              :options="brandOptions"
            />
            <BaseSelectField
              v-model="dependencia"
              label="Dependency"
              :options="dependenciaOptions"
            />

            <div class="supersessions">
              <label class="text-sm">Supersessions (alternative codes)</label>
              <div class="supersession-input-row">
                <input
                  v-model="newSupersession"
                  type="text"
                  class="bordered flex-1"
                  placeholder="Alternative code"
                  @keydown.enter.prevent="addSupersession"
                />
                <button
                  type="button"
                  class="btn-dark-button px-3"
                  @click="addSupersession"
                >
                  Add
                </button>
              </div>
              <div v-if="supersessions.length" class="chips">
                <span v-for="code in supersessions" :key="code" class="chip">
                  {{ code }}
                  <button
                    type="button"
                    class="chip-remove"
                    @click="removeSupersession(code)"
                  >
                    ×
                  </button>
                </span>
              </div>
              <p class="text-xs text-gray-500">
                Added to unificado <b>{{ unificado || "—" }}</b> after the
                product is created.
              </p>
            </div>
          </div>

          <div v-if="isCatalog" class="col-catalog">
            <label class="text-sm font-semibold">Catalog pricing</label>
            <CatalogPriceForm
              v-model="catalogDraft"
              :priceGroups="priceGroups"
              :dealerProfit="dealerProfit"
              :masterListPrice.sync="catalogListPrice"
              masterListPriceEditable
            />
          </div>
        </div>

        <button
          class="btn-info-button px-8 mt-2 w-full"
          :disabled="loading || validatingCode"
        >
          <span v-if="!loading">Save</span>
          <base-mini-spinner v-else></base-mini-spinner>
        </button>
      </BaseForm>
    </div>
  </BaseModal>
</template>
<style lang="scss">
#new-product-modal .modal-content {
  max-width: none;
  width: 100%;
}
.form-layout {
  &.two-col {
    display: grid;
    grid-template-columns: minmax(300px, 1fr) 2fr;
    gap: 24px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }
}
.response {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: 600;

  @apply rounded-md p-2;
}
.response-error {
  @apply bg-red-100 text-red-600;
}
.response-success {
  @apply bg-green-100 text-green-600;
}
.supersessions {
  @apply my-2;
  .supersession-input-row {
    display: flex;
    gap: 8px;
    align-items: center;
    margin: 4px 0;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 6px 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #e5e7eb;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 0.85rem;
  }
  .chip-remove {
    font-weight: 700;
    line-height: 1;
    color: #6b7280;
  }
}
</style>
